import React from 'react';
import type { BlockInstance, TemplateModel, WebsitePage } from '@troia-v3/types';
import { Icon } from '@/shared/icons';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { canAddBlock, getPageName, sortBlocksByOrder } from './types';

interface PanelPageProps {
  page: WebsitePage;
  model: TemplateModel;
  onOpenSeo: () => void;
  onOpenBlock: (instanceId: string) => void;
  onToggleVisible: (instanceId: string) => void;
  onMoveBlock: (instanceId: string, direction: 'up' | 'down') => void;
  onDuplicateBlock: (instanceId: string) => void;
  onRemoveBlock: (instanceId: string) => void;
  onOpenAddBlock: () => void;
  hasPresets: boolean;
  onOpenChooseLayout: () => void;
}

type BlockRegion = 'top' | 'main' | 'sidebar' | 'bottom';

const REGION_LABELS: Record<BlockRegion, string> = {
  top: 'Topo',
  main: 'Conteúdo principal',
  sidebar: 'Barra lateral',
  bottom: 'Rodapé',
};

const REGION_ORDER: BlockRegion[] = ['top', 'main', 'sidebar', 'bottom'];

/** Painel de uma página: SEO + lista de blocos (olho inline + menu ⋮) + adicionar. */
export const PanelPage: React.FC<PanelPageProps> = ({
  page, model, onOpenSeo, onOpenBlock, onToggleVisible, onMoveBlock,
  onDuplicateBlock, onRemoveBlock, onOpenAddBlock, hasPresets, onOpenChooseLayout,
}) => {
  const sorted = sortBlocksByOrder(page.blocks);

  /** Derive region for every block in the page. Returns null when no block has a region (static page). */
  const blockRegionMap = React.useMemo<Map<string, BlockRegion> | null>(() => {
    const map = new Map<string, BlockRegion>();
    let hasAnyRegion = false;
    for (const block of sorted) {
      const def = model.blocks.find((b) => b.id === block.blockId);
      if (def?.region) {
        hasAnyRegion = true;
        map.set(block.instanceId, def.region);
      }
    }
    return hasAnyRegion ? map : null;
  }, [sorted, model.blocks]);

  const renderBlockItem = (
    block: BlockInstance,
    indexInGroup: number,
    groupLength: number,
  ) => {
    const def = model.blocks.find((b) => b.id === block.blockId);
    const canDuplicate = canAddBlock(model, page, block.blockId);

    const disableUp = indexInGroup === 0;
    const disableDown = indexInGroup === groupLength - 1;

    // Menu de ações do bloco — CardActionsMenu shared (F78: portal, danger token)
    const actions: CardAction[] = [
      {
        label: canDuplicate ? 'Duplicar' : 'Duplicar (no limite)',
        icon: 'copy',
        onClick: () => onDuplicateBlock(block.instanceId),
        disabled: !canDuplicate,
      },
      {
        label: 'Mover para cima',
        icon: 'chevron-up',
        onClick: () => onMoveBlock(block.instanceId, 'up'),
        disabled: disableUp,
      },
      {
        label: 'Mover para baixo',
        icon: 'chevron-down',
        onClick: () => onMoveBlock(block.instanceId, 'down'),
        disabled: disableDown,
      },
      ...(block.addedByUser
        ? [{
            label: 'Remover',
            icon: 'trash',
            onClick: () => onRemoveBlock(block.instanceId),
            variant: 'danger',
            dividerBefore: true,
          } satisfies CardAction]
        : []),
    ];

    return (
      <div
        key={block.instanceId}
        className="relative flex items-center gap-1 px-2 py-2 rounded-lg border transition-opacity"
        style={{
          borderColor: 'var(--theme-card-border)',
          backgroundColor: 'var(--theme-card-background)',
          opacity: block.visible ? 1 : 0.5,
        }}
        data-testid={`block-item-${block.instanceId}`}
      >
        <button
          type="button"
          onClick={() => onOpenBlock(block.instanceId)}
          className="flex-1 flex items-center gap-2 min-w-0 text-left"
          data-testid={`block-open-${block.instanceId}`}
        >
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-medium truncate" style={{ color: 'var(--theme-card-text)' }}>
              {def?.name || block.blockId}
            </span>
            <span className="block text-[11px] truncate" style={{ color: 'var(--theme-muted-text)' }}>
              {block.mirrorOf ? (
                <span className="inline-flex items-center gap-1" style={{ color: 'var(--theme-primary)' }}>
                  <Icon name="link" className="w-3 h-3" />
                  espelha {getPageName(model, block.mirrorOf)}
                </span>
              ) : (
                def?.category || ''
              )}
            </span>
          </span>
        </button>

        <button
          type="button"
          onClick={() => onToggleVisible(block.instanceId)}
          className="p-1 rounded hover:opacity-70"
          style={{ color: block.visible ? 'var(--theme-primary)' : 'var(--theme-muted-text)' }}
          title={block.visible ? 'Ocultar bloco' : 'Exibir bloco'}
          data-testid={`block-visibility-${block.instanceId}`}
        >
          <Icon name={block.visible ? 'eye' : 'eye-off'} className="w-3.5 h-3.5" />
        </button>

        <CardActionsMenu
          actions={actions}
          size="sm"
          data-testid={`block-menu-${block.instanceId}`}
        />
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={onOpenSeo}
        className="w-full flex items-center gap-2.5 px-2.5 py-2.5 rounded-lg border transition-colors text-left"
        style={{ borderColor: 'var(--theme-card-border)', color: 'var(--theme-card-text)' }}
        data-testid="page-seo-item"
      >
        <Icon name="search" className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
        <span className="flex-1 text-sm font-medium">SEO da página</span>
        <Icon name="chevron-right" className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
      </button>

      {hasPresets && (
        <button
          type="button"
          onClick={onOpenChooseLayout}
          className="w-full flex items-center gap-2.5 px-2.5 py-2.5 rounded-lg border transition-colors text-left"
          style={{ borderColor: 'var(--theme-card-border)', color: 'var(--theme-card-text)' }}
          data-testid="page-choose-layout"
        >
          <Icon name="grid" className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
          <span className="flex-1 text-sm font-medium">Escolher layout</span>
          <Icon name="chevron-right" className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
        </button>
      )}

      <p className="text-[11px] font-medium uppercase tracking-wider px-1" style={{ color: 'var(--theme-muted-text)' }}>
        Blocos
      </p>

      {blockRegionMap === null ? (
        /* Static page — flat list (existing behavior) */
        <div className="space-y-1">
          {sorted.map((block, index) =>
            renderBlockItem(block, index, sorted.length)
          )}
        </div>
      ) : (
        /* Dynamic page — grouped by region */
        <div className="space-y-4">
          {REGION_ORDER.map((region) => {
            const regionBlocks = sorted.filter(
              (b) => (blockRegionMap.get(b.instanceId) ?? 'main') === region,
            );
            if (regionBlocks.length === 0) return null;
            return (
              <div key={region} data-testid={`region-group-${region}`}>
                <p
                  className="text-[10px] font-semibold uppercase tracking-widest px-1 mb-1.5"
                  style={{ color: 'var(--theme-muted-text)' }}
                >
                  {REGION_LABELS[region]}
                </p>
                <div className="space-y-1">
                  {regionBlocks.map((block, indexInRegion) =>
                    renderBlockItem(block, indexInRegion, regionBlocks.length)
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <button
        type="button"
        onClick={onOpenAddBlock}
        className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border border-dashed transition-colors text-sm font-medium"
        style={{ borderColor: 'var(--theme-primary)', color: 'var(--theme-primary)' }}
        data-testid="block-add-button"
      >
        <Icon name="plus" className="w-4 h-4" />
        Adicionar bloco
      </button>
    </div>
  );
};
